import type { DeepPartial } from "@src/app/types/bff";
import { BFF_ENDPOINT } from "@src/app/utils/bff";
import type { MetaFunction } from "react-router";

/** meta info for the page */
export type MetaInfo = DeepPartial<{
  title: string;
  description: string;
  canonical: string;
  keywords: string[];
  section: string;
  author: string;
  /**
   * @default "index, follow"
   */
  robots: string;

  /**
   * og tags
   * @tutorial https://ogp.me/
   */
  ogInfo: {
    ogTitle: string;
    ogDescription: string;
    ogType: string;
    ogUrl: string;
    ogImage: string;

    twitterCard: string;
    twitterTitle: string;
    twitterDescription: string;
    twitterImage: string;
    twitterUrl: string;
  };

  isArticlePage?: boolean;
}>;

const defaultMetaInfo: MetaInfo = {
  title: "ThinkChina - Big Reads, Opinions & Columns on China",
  description:
    "ThinkChina - Get latest insights on China through the eyes of global thought leaders & journalists.",
  canonical: "https://www.thinkchina.sg",
  keywords: [],
  robots: "index, follow",

  ogInfo: {
    ogTitle: "",
    ogDescription: "",
    ogType: "website",
    ogUrl: "",
    ogImage: `${BFF_ENDPOINT}/logo@2x.svg`,

    twitterCard: "summary_large_image",
    twitterTitle: "",
    twitterDescription: "",
    twitterImage: "",
    twitterUrl: "",
  },
};

/**
 * @see https://docs.google.com/spreadsheets/d/1JENgXBy5uUzs-1M6LAjYCfFUGp6qBzWmx0qa4zB8PQg/edit?gid=1514826223#gid=1514826223
 */
export const createMetaTags = (props?: MetaInfo): ReturnType<MetaFunction> => {
  const _metaInfo = { ...defaultMetaInfo, ...props };
  const _ogInfo = { ...defaultMetaInfo.ogInfo, ...props?.ogInfo };

  const isArticlePage = props?.isArticlePage || false;
  const keywords = props?.keywords || [];
  const section = props?.section || "";
  const author = props?.author || "";

  return [
    { title: _metaInfo.title },
    { name: "description", content: _metaInfo.description },
    { name: "robots", content: _metaInfo.robots },
    { name: "keywords", content: _metaInfo.keywords },

    // { rel: "alternate"}, // remove in Phase3
    { tagName: "link", rel: "canonical", href: _metaInfo.canonical },
    // { rel: "shortlink",  }, // remove in Phase3
    { name: "format-detection", content: "telephone=no" },

    // google-site-verification
    {
      name: "google-site-verification",
      content: "yE8InBB5xS9wFHBukGOmijn1jY0-jRkvCsmEcCjwBuc",
    },

    // Open Graph tags
    { property: "og:title", content: _ogInfo.ogTitle || _metaInfo.title },
    { property: "og:type", content: _ogInfo.ogType },
    { property: "og:image", content: _ogInfo.ogImage },
    { property: "og:url", content: _ogInfo.ogUrl || _metaInfo.canonical },

    {
      property: "og:description",
      content: _ogInfo.ogDescription || _metaInfo.description,
    },
    { property: "og:locale", content: "en_SG" },
    {
      property: "og:site_name",
      content: "ThinkChina - Big Reads, Opinions & Columns on China",
    },

    // Article Page
    isArticlePage ? { property: "article:author", content: author } : {},
    isArticlePage ? { property: "article:section", content: section } : {},
    {
      ...(isArticlePage
        ? keywords.map((i) => ({ property: "article:tag", content: i }))
        : []),
    },

    // Twitter tags
    { property: "twitter:card", content: _ogInfo.twitterCard },
    {
      property: "twitter:title",
      content: _ogInfo.twitterTitle || _ogInfo.ogTitle || _metaInfo.title,
    },
    {
      property: "twitter:url",
      content: _ogInfo.twitterUrl || _ogInfo.ogUrl || _metaInfo.canonical,
    },
    {
      property: "twitter:description",
      content:
        _ogInfo.twitterDescription ||
        _ogInfo.ogDescription ||
        _metaInfo.description,
    },
    {
      property: "twitter:image",
      content: _ogInfo.twitterImage || _ogInfo.ogImage,
    },
  ];
};
